import React, {ReactNode, useCallback} from "react";
import {Alert, AppBar, Grid2, IconButton, PaletteMode, Tooltip, Typography, useTheme} from "@mui/material";
import {Home, LocationOn, UnfoldLessOutlined, UnfoldMoreOutlined} from "@mui/icons-material";
import {isAdminRole, isClientRole, isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {AppPaths} from "@/app/AppPaths";
import {useDialogController} from "@/lib/dialog-controller";
import {MenuButton, MenuButtonItem} from "@/lib/components/buttons/MenuButton";
import {ChangePasswordDialog} from "@/app/header/ChangePasswordDialog";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {AdminI18N, CommonI18N, ReportI18N} from "@/translations";
import {useTranslation} from "react-i18next";
import {AdminDialog} from "@/app/header/AdminDIalog";
import {LogViewerDialog} from "@/app/header/LogViewerDialog";
import {CompanySwitcher} from "@/app/header/CompanySwitcher";
import {
    AbmeldenIcon,
    AdminConsoleIcon,
    AnmeldenIcon,
    BoxHubLogo,
    BoxIcon,
    ExterneAnfragenIcon,
    GebaeudeIcon,
    InterneAnfragenIcon,
    KladdeIcon,
    LogviewerIcon,
    MondIcon,
    PasswortAendernIcon,
    PersonIcon,
    PlusIcon,
    SonneIcon,
    WeltkugelIcon
} from "@/agentur/AgenturIcons";
import {color_blue, color_grey} from "@/agentur/AgenturColors";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {VisitorLanguageSwitch} from "@/app/locale/VisitorLanguageSwitch";
import {showsVisitorLanguageSwitch} from "@/app/locale/visitorLanguage";
import {useScreenClass} from "@/lib/screen-class";

/**
 * The sign-in action of an anonymous visitor. Below 600 px it shows only its icon, with the label as tooltip and
 * accessible name: at 360 px the language switch does not fit next to the full label.
 */
const SignInButton = ({mode, iconOnly}: { mode?: PaletteMode; iconOnly: boolean }) => {
    const {t} = useTranslation();
    const label = t(CommonI18N.header.action.signIn);
    if (iconOnly) {
        return (
                <Tooltip title={label}>
                    <IconButton component={Link} to={AppPaths.SIGN_IN} aria-label={label} size="small"
                                data-cy="signin-button">
                        <AnmeldenIcon mode={mode}/>
                    </IconButton>
                </Tooltip>
        );
    }
    return (
            <ButtonEx
                    label={label}
                    startIcon={<AnmeldenIcon mode={mode}/>}
                    color="primary"
                    variant="contained"
                    to={AppPaths.SIGN_IN}
                    data-cy="signin-button"
            />
    );
};

/**
 * The header actions of an anonymous visitor: the visitor language switch (only on the pages its rule names) in
 * front of the sign-in action. Logo, switch and sign-in stay on one row down to 360 px.
 */
const AnonymousHeaderActions = ({mode}: { mode?: PaletteMode }) => {
    const {pathname} = useLocation();
    const narrow = useScreenClass() === "narrow";
    return (
            <>
                {showsVisitorLanguageSwitch(false, pathname) && (
                        <Grid2>
                            <VisitorLanguageSwitch/>
                        </Grid2>
                )}
                <Grid2>
                    <SignInButton mode={mode} iconOnly={narrow}/>
                </Grid2>
            </>
    );
};

interface HeaderProps {
    mode?: PaletteMode;
    toggleColorMode?: () => void;
    expanded?: boolean;
    setExpanded?: (expanded: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({mode, toggleColorMode, expanded, setExpanded}) => {
    const {t} = useTranslation();
    const {authenticated, roles, logout, user, company, environment, timeDisplacement} = useAuth();
    const admin = isAdminRole(roles);
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [DialogNode, showDialog] = useDialogController();

    const doLogout = useCallback(async () => {
        await logout();
        navigate(AppPaths.SIGN_IN, {replace: true});
    }, [logout, navigate]);

    const handleLogout = useCallback(() => {
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={doLogout}
                        title={t(CommonI18N.header.action.logout)}
                        message={t(CommonI18N.header.message.logout)}
                        confirmButtonText={t(CommonI18N.header.action.logout)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
                        maxWidth="sm"
                        fullWidth
                />
        ));
    }, [doLogout, showDialog, t]);

    const handleChangePassword = useCallback(() => {
        showDialog((open, close) => <ChangePasswordDialog open={open} onClose={close}/>);
    }, [showDialog]);

    const handleAdminConsole = useCallback(() => {
        showDialog((open, close) => <AdminDialog open={open} onClose={close}/>);
    }, [showDialog]);

    const handleLogViewer = useCallback(() => {
        showDialog((open, close) => <LogViewerDialog open={open} onClose={close}/>);
    }, [showDialog]);

    const isCurrentPath = (path: string) => {
        const parts = path.split("?");
        if (parts.length > 1) {
            return location.pathname === parts[0] && location.search === `?${parts[1]}`;
        }
        return location.pathname === path;
    };

    const menu = useCallback(() => {
        const items: MenuButtonItem[] = [
            {
                label: t(CommonI18N.header.action.myProfile),
                icon: <PersonIcon mode={theme.palette.mode}/>,
                content: "link",
                to: user?.id ? AppPaths.USER_fn(user.id, company?.id) : undefined,
                useRouter: true
            },
            {
                label: t(CommonI18N.header.action.myCompany),
                icon: <Home mode={theme.palette.mode}/>,
                content: "link",
                to: company?.id ? AppPaths.COMPANY_fn(company?.id) : undefined,
                useRouter: true
            },
            {
                label: t(AdminI18N.companyRelationships.page.title),
                icon: <GebaeudeIcon mode={theme.palette.mode}/>,
                content: "link",
                to: AppPaths.SETTINGS_COMPANIES,
                useRouter: true,
                "data-cy": "settings-companies-menu-item"
            },
            {
                label: t(CommonI18N.header.action.adminConsole),
                icon: <AdminConsoleIcon mode={theme.palette.mode}/>,
                content: handleAdminConsole,
                hidden: !admin || environment === "prod"
            },
            {
                label: t(CommonI18N.header.action.logViewer),
                icon: <LogviewerIcon mode={theme.palette.mode}/>,
                content: handleLogViewer,
                hidden: !admin
            },
            {
                label: t(CommonI18N.header.action.changePassword),
                icon: <PasswortAendernIcon mode={theme.palette.mode}/>,
                content: handleChangePassword
            },
            {
                content: "divider"
            },
            {
                label: t(CommonI18N.header.action.logout),
                icon: <AbmeldenIcon mode={theme.palette.mode}/>,
                content: handleLogout,
                "data-cy": "logout-button"
            }
        ];
        return items;
    }, [
        t,
        theme.palette.mode,
        user?.id,
        company?.id,

        handleAdminConsole,
        admin,
        environment,
        roles,
        handleLogViewer,
        handleChangePassword,
        handleLogout
    ]);

    const selectColor = mode === "dark" ? color_blue[2] : color_blue[3];

    const HeaderLink = ({
                            label,
                            path,
                            icon,
                            "data-cy": dataCy
                        }: {
        label: string;
        path: string;
        icon: ReactNode;
        "data-cy"?: string;
    }) => {
        const selected = isCurrentPath(path);
        return (
                <Link to={selected ? undefined : (path as any)} style={{textDecoration: "none"}}>
                    <Grid2
                            container
                            spacing={1}
                            alignItems="center"
                            sx={{
                                height: "100%",
                                py: 1,
                                px: 5,
                                backgroundColor: selected ? selectColor : undefined,
                                color: theme.palette.text.primary,
                                "&>span": {
                                    textTransform: "uppercase",
                                    fontSize: "68%",
                                    fontWeight: "bold"
                                }
                            }}
                    >
                        {icon}
                        <span className="menu-name">{label}</span>
                    </Grid2>
                </Link>
        );
    };


    const buttons =
            (isClientRole(roles) && (
                    <>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.bidderOverview)}
                                    path={AppPaths.BIDDER_OVERVIEW}
                                    icon={<ExterneAnfragenIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.requesterOverview)}
                                    path={AppPaths.REQUESTER_OVERVIEW}
                                    icon={<InterneAnfragenIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.createRequest)}
                                    path={AppPaths.INTERNAL_REQUEST_fn("create")}
                                    icon={<PlusIcon mode={mode}/>}
                            />
                        </Grid2>
                    </>
            )) ||
            (isOperatorRole(roles) && (
                    <>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.company)}
                                    path={AppPaths.COMPANY_OVERVIEW}
                                    data-cy="company-overview-button"
                                    icon={<GebaeudeIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(AdminI18N.companies.page.registrationRequestsTitle)}
                                    path={AppPaths.COMPANY_UNVERIFIED}
                                    data-cy="company-unverified-button"
                                    icon={<KladdeIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.cartonGradeImports)}
                                    path={AppPaths.CARTON_GRADES}
                                    data-cy="carton-grades-button"
                                    icon={<BoxIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.addresses)}
                                    path={AppPaths.ADMIN_ADDRESSES}
                                    data-cy="admin-addresses-button"
                                    icon={<LocationOn/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.languageFiles)}
                                    path={AppPaths.LANGUAGE_FILES}
                                    data-cy="language-files-button"
                                    icon={<WeltkugelIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(CommonI18N.header.action.operatorRequests)}
                                    path={AppPaths.OPERATOR_REQUEST_OVERVIEW}
                                    data-cy="dashboard-button"
                                    icon={<ExterneAnfragenIcon mode={mode}/>}
                            />
                        </Grid2>
                        <Grid2>
                            <HeaderLink
                                    label={t(ReportI18N.page.title)}
                                    path={AppPaths.ADMIN_REPORTS}
                                    data-cy="reports-button"
                                    icon={<KladdeIcon mode={mode}/>}
                            />
                        </Grid2>
                    </>
            )) ||
            undefined;

    return (
            <AppBar
                    position="relative"
                    variant="outlined"
                    sx={{
                        backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : color_grey[5],
                        color: theme.palette.text.primary,
                        borderLeft: "none",
                        borderRight: "none",
                        borderBottom: `1px solid ${theme.palette.primary.main}`,
                        zIndex: theme.zIndex.drawer + 1
                    }}
            >
                <Grid2
                        container
                        spacing={authenticated ? 2 : {xs: 1, sm: 2}}
                        alignItems="center"
                        minHeight="52px"
                        wrap={authenticated ? "wrap" : "nowrap"}
                >
                    <Grid2 paddingBottom="4px" flexShrink={authenticated ? undefined : 0}>
                        <Link to={AppPaths.LANDING_PAGE} style={{display: "flex"}}>
                            <BoxHubLogo mode={theme.palette.mode}/>
                        </Link>
                    </Grid2>

                    <Grid2
                            container
                            spacing={0}
                            flexGrow={1}
                            sx={{
                                "&>div": {
                                    borderLeft: `1px solid ${theme.palette.primary.main}`,
                                    height: "48px"
                                },
                                "&>div:last-child": {
                                    borderRight: `1px solid ${theme.palette.primary.main}`
                                },
                                "& .menu-name": {letterSpacing: "0.05em"}
                            }}
                    >
                        {buttons}
                    </Grid2>

                    {timeDisplacement ? (
                            <Grid2>
                                <Alert variant="filled" severity="info" color="warning">
                                    Zeitverschiebung: {timeDisplacement} Tage:{" "}
                                    {new Date(Date.now() + timeDisplacement * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                </Alert>
                            </Grid2>
                    ) : null}

                    <Grid2
                            container
                            spacing={authenticated ? 0 : 1}
                            alignItems="center"
                            flexShrink={authenticated ? undefined : 0}
                            paddingRight={authenticated ? undefined : 1}
                    >
                        {toggleColorMode && (
                                <Grid2>
                                    <IconButtonEx
                                            onClick={toggleColorMode}
                                            size="small"
                                            icon={mode === "dark" ? <MondIcon mode="dark"/> : <SonneIcon mode="light"/>}
                                    />
                                </Grid2>
                        )}

                        {setExpanded && (
                                <Grid2>
                                    <IconButtonEx
                                            Icon={expanded ? UnfoldLessOutlined : UnfoldMoreOutlined}
                                            rotate="90deg"
                                            onClick={() => setExpanded(!expanded)}
                                    />
                                </Grid2>
                        )}

                        {authenticated ? (
                                <>
                                    <Grid2>
                                        <CompanySwitcher/>
                                    </Grid2>
                                    <Grid2>
                                        <MenuButton
                                                icon={<PersonIcon mode={mode}/>}
                                                noUpperText
                                                text={
                                                    <Grid2 container direction="column">
                                                        <Grid2>
                                                            <Tooltip
                                                                    title={`${user?.firstName || ""} ${user?.lastName || ""}`}>
                                                                <Typography
                                                                        variant="body1"
                                                                        paddingTop="4px"
                                                                        marginBottom="-10px"
                                                                        fontWeight="500"
                                                                        sx={{
                                                                            maxWidth: "200px",
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis"
                                                                        }}
                                                                >
                                                                    {user?.firstName} {user?.lastName}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Grid2>
                                                        <Grid2>
                                                            <Tooltip title={company?.name || ""}>
                                                                <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            maxWidth: "200px",
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis"
                                                                        }}
                                                                >
                                                                    {company?.name}
                                                                </Typography>
                                                            </Tooltip>
                                                        </Grid2>
                                                    </Grid2>
                                                }
                                                menu={menu}
                                        />
                                    </Grid2>
                                </>
                        ) : (
                                <AnonymousHeaderActions mode={mode}/>
                        )}
                    </Grid2>
                </Grid2>
                <DialogNode/>
            </AppBar>
    );
};
